import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RouterParams } from '../../../app/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';
import useNavigationPage from '../../../hooks/useNavigationPage';
import { BusinessNameDataForm, defaultBusinessNameForm } from '../../../app/models/BusinessName';
import useBusinessNamesService from '../../../app/services/hooks/useBusinessService';
import BusinessNameContactContainer from '../components/BusinessNameContactContainer';
import BusinessNameBankAccountContainer from '../components/BusinessNameBankAccountContainer';
import { Tab, Tabs } from 'react-bootstrap';

const BusinessNameShow = () => {
    const { navigationPage } = useNavigationPage();
    const { hideLoading } = useContext(AppContext);
    const { id } = useParams<RouterParams>();

    const breadcrumbs: BreadcrumbsItem[] = [
        {
            name: 'Razones Sociales',
            url: '/business-names',
            isActive: false
        },
        {
            name: 'Ver razón social #' + id,
            url: '/business-names/' + id + '/show',
            isActive: true
        }
    ];

    const {
        showBusinessName,
        fetchingShowBusinessName,
    } = useBusinessNamesService();

    const [businessName, setBusinessName] = useState<BusinessNameDataForm>(defaultBusinessNameForm);

    useEffect(() => {
        loadBusinessName(parseInt(id));
    }, [id]);

    const loadBusinessName = (id: number) => {
        showBusinessName(id, {
            onSuccess: (response: ServiceResponse) => {
                setBusinessName(response.data.business_name);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data?.to_dashboard) {
                    navigationPage('/business-names');
                }
            }
        });
    };

    const getLogoUrl = () => {
        if (businessName.logo_url) {
            return businessName.logo_url;
        }
        return 'https://static.vecteezy.com/system/resources/previews/009/292/244/original/default-avatar-icon-of-social-media-user-vector.jpg';
    };

    return (
        <>
            <Breadcrumbs pageSection={`Ver razón social #${id}`} breadcrumbs={breadcrumbs} />
            <div className="row">
                <div className="col-md-6">
                    <DefaultCard>
                        <div className="h3 mt-0 mb-2 card-title">Datos Generales</div>
                        {!fetchingShowBusinessName ? (
                            <>
                                <div className="col-md-12 text-center d-flex mb-3">
                                    <div className="mx-auto">
                                        <img
                                            src={getLogoUrl()}
                                            alt="Logo"
                                            style={{
                                                width: '150px',
                                                height: '150px',
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                border: '2px solid #ddd'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">ID:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">{businessName.id || '-'}</div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Nombre:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">{businessName.name || '-'}</div>
                                    </div>
                                </div>

                                {businessName.is_national && (
                                    <div className="mb-2 row">
                                        <div className="col-md-4">
                                            <label className="col-form-label font-weight-bold">RUT:</label>
                                        </div>
                                        <div className="col-md-8">
                                            <div className="form-control-plaintext">{businessName.rut || '-'}</div>
                                        </div>
                                    </div>
                                )}

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Nombre de Fantasía:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">{businessName.fantasy_name || '-'}</div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Email:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">{businessName.email || '-'}</div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Teléfono:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">
                                            {businessName.phone_code || ''} {businessName.phone || '-'}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Dirección:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">{businessName.address || '-'}</div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">País:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">{businessName.country || '-'}</div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Sitio Web:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">
                                            {businessName.website ? (
                                                <a href={businessName.website} target="_blank" rel="noopener noreferrer">
                                                    {businessName.website}
                                                </a>
                                            ) : '-'}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Es Nacional:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">
                                            {businessName.is_national ? 'Sí' : 'No'}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Activo:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">
                                            <span className={`badge ${businessName.is_active ? 'badge-success' : 'badge-danger'}`}>
                                                {businessName.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Tipo de Entidad:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">
                                            {[
                                                businessName.has_society && 'Sociedad',
                                                businessName.has_provider && 'Proveedor',
                                                businessName.has_client && 'Cliente'
                                            ].filter(Boolean).join(', ') || '-'}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Fecha de Creación:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">
                                            {businessName.created_at ? new Date(businessName.created_at).toLocaleString('es-CL') : '-'}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-2 row">
                                    <div className="col-md-4">
                                        <label className="col-form-label font-weight-bold">Última Modificación:</label>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-control-plaintext">
                                            {businessName.updated_at ? new Date(businessName.updated_at).toLocaleString('es-CL') : '-'}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <LazyLoading />
                        )}
                    </DefaultCard>
                </div>
                <div className="col-md-6">
                    {/* Espacio para información adicional si es necesario */}
                </div>
            </div>
            <Tabs defaultActiveKey="bank-accounts" id="business-name-tabs" className="mb-3">
                <Tab eventKey="bank-accounts" title="Cuentas bancarias">
                    <BusinessNameBankAccountContainer 
                        businessNameId={parseInt(id)} 
                    />
                </Tab>
                <Tab eventKey="business-name-contacts" title="Contactos">
                    <BusinessNameContactContainer
                        businessNameId={parseInt(id)}
                    />
                </Tab>
            </Tabs>
        </>
    );
};

export default BusinessNameShow;

